import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Image + caption: a photo with an optional caption. Saved as a bare block placeholder with
// data-image-src (the CDN URL the upload endpoint returned), data-image-alt, data-image-caption and
// data-image-height (px, 80..1200, 400 when unset; dragged with the handle under the
// preview); Newsletters::RenderBlocks renders it, and only for a URL on our own upload CDN
// (Newsletters::BlockImageUrl). The node is named imageBlock so it never clashes with the plain @tiptap image
// node that pasted images still use. The editor chrome lives in dashboard/shared/tiptap/_image_editor.html.erb
// and the preview comes from the same server component. The block shows one view at a time (block_views.js):
// the editor (upload, fields, alt text) or the preview; an image opens in its preview, a new block in the editor.
//
// The page passes data-tiptap-block-capabilities-value.image = { post_id, upload_path, alt_text_path }.
// alt_text_path is only present when AI is on for the podcast; without it there is simply no suggestion row.
// Alt text is always an ordinary visible field: after an upload the editor posts the same file's bytes to
// alt_text_path and, when a draft comes back, offers it with a "Use" button instead of filling the field.

const PREVIEW_DELAY_MS = 300;
const DEFAULT_HEIGHT = 400;
const MIN_HEIGHT = 80;
const MAX_HEIGHT = 1200;
const DROPZONE_LABEL = 'Drop an image, or choose one';

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const postFile = async (url, file, extra = {}) => {
  const body = new FormData();
  body.append('file', file);
  Object.entries(extra).forEach(([key, value]) => body.append(key, value));
  try {
    const res = await fetch(url, {
      method: 'POST',
      body,
      credentials: 'include',
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    });
    return res.ok ? await res.json() : null;
  } catch (_) {
    return null;
  }
};

export const ImageBlock = Node.create({
  name: 'imageBlock',

  ...blockNodeSpec('image', {
    attributes: {
      imageSrc: dataAttribute('imageSrc'),
      imageAlt: dataAttribute('imageAlt'),
      imageCaption: dataAttribute('imageCaption'),
      imageHeight: dataAttribute('imageHeight', { default: null }),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      capabilities: null,      // { post_id, upload_path, alt_text_path } — injected by the tiptap controller
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      const capabilities = extension.options.capabilities || {};
      let currentNode = node;
      let previewTimer = null;
      let previewKey = null; // the attrs the preview shows, minus the height, which is resized in place
      let previewRequest = 0;
      let suggestion = '';
      let drafting = false;
      let destroyed = false;

      const dom = document.createElement('div');
      dom.setAttribute('data-image-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Image</div>';

      const $ = selector => dom.querySelector(selector);
      const fileInput = $('[data-file-input]');
      const dropzone = $('[data-dropzone]');
      const dropzoneLabel = $('[data-dropzone-label]');
      const uploading = $('[data-uploading]');
      const errorLine = $('[data-error]');
      const filled = $('[data-filled]');
      const preview = $('[data-preview]');
      const previewLoading = $('[data-preview-loading]');
      const previewWrap = $('[data-preview-wrap]');
      const resizeHandle = $('[data-resize-handle]');
      const previewMissing = $('[data-preview-missing]');
      const replaceButton = $('[data-replace]');
      const captionField = $('[data-field="caption"]');
      const altField = $('[data-field="alt"]');
      const aiRow = $('[data-ai]');
      const suggestionText = $('[data-suggestion]');
      const draftingRow = $('[data-drafting]');
      const altHint = $('[data-alt-hint]');
      const altWarning = $('[data-alt-warning]');

      const hasImage = () => Boolean(currentNode.attrs.imageSrc);

      // Done needs an uploaded image.
      const views = blockViews(dom, {
        canFinish: hasImage,
        onChange: view => { if (view === 'preview') schedulePreview(); },
      });

      const setAttrs = attrs => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, ...attrs }));
      };

      const showError = message => {
        if (!errorLine) return;
        errorLine.textContent = message || '';
        errorLine.classList.toggle('hidden', !message);
      };

      const render = () => {
        const image = hasImage();
        const alt = (currentNode.attrs.imageAlt || '').trim();
        dropzone?.classList.toggle('hidden', image);
        filled?.classList.toggle('hidden', !image);
        const showSuggestion = image && Boolean(suggestion) && alt !== suggestion;
        aiRow?.classList.toggle('hidden', !showSuggestion);
        if (suggestionText) suggestionText.textContent = suggestion;
        draftingRow?.classList.toggle('hidden', !(image && drafting));
        altWarning?.classList.toggle('hidden', !image || alt !== '');
        altHint?.classList.toggle('hidden', image && alt === '');
        views?.refresh();
      };

      const clampHeight = value => Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(value)));
      const currentHeight = () => {
        const raw = parseInt(currentNode.attrs.imageHeight, 10);
        return Number.isFinite(raw) ? clampHeight(raw) : DEFAULT_HEIGHT;
      };

      // The spinner covers the whole wait: the debounce, the server render and the <img> load itself.
      const setPreviewLoading = loading => {
        previewLoading?.classList.toggle('hidden', !loading);
        previewWrap?.classList.toggle('hidden', loading);
      };

      const showPreview = html => {
        if (!preview) return;
        preview.innerHTML = html || '';
        previewMissing?.classList.toggle('hidden', Boolean(html) || !hasImage());
        const img = preview.querySelector('img');
        if (!html || !img) {
          setPreviewLoading(false);
          return;
        }
        const done = () => { if (!destroyed) setPreviewLoading(false); };
        if (img.complete) done();
        else {
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }
      };

      const previewImage = () => preview?.querySelector('img');

      resizeHandle?.addEventListener('pointerdown', e => {
        const img = previewImage();
        if (!img) return;
        e.preventDefault();
        resizeHandle.setPointerCapture?.(e.pointerId);
        const startY = e.clientY;
        const startHeight = img.getBoundingClientRect().height || currentHeight();
        let height = clampHeight(startHeight);
        const move = ev => {
          height = clampHeight(startHeight + ev.clientY - startY);
          img.style.height = `${height}px`;
          img.style.maxHeight = `${height}px`;
        };
        const end = () => {
          resizeHandle.removeEventListener('pointermove', move);
          resizeHandle.removeEventListener('pointerup', end);
          resizeHandle.removeEventListener('pointercancel', end);
          setAttrs({ imageHeight: height });
        };
        resizeHandle.addEventListener('pointermove', move);
        resizeHandle.addEventListener('pointerup', end);
        resizeHandle.addEventListener('pointercancel', end);
      });

      const schedulePreview = () => {
        if (!extension.options.fetchRenderedHtml || views?.view !== 'preview') return;
        clearTimeout(previewTimer);
        if (!hasImage()) {
          previewKey = null;
          showPreview('');
          return;
        }
        // A resize only changes the height, which the dragged image already shows.
        const shown = { ...currentNode.attrs };
        delete shown.imageHeight;
        const key = JSON.stringify(shown);
        if (key === previewKey && previewImage()) return;
        previewKey = key;
        setPreviewLoading(true);
        previewTimer = setTimeout(async () => {
          const request = ++previewRequest;
          const html = await extension.options.fetchRenderedHtml({ ...currentNode.attrs });
          if (request === previewRequest && !destroyed) {
            if (!html) previewKey = null;
            showPreview(html);
          }
        }, PREVIEW_DELAY_MS);
      };

      const syncFromNode = () => {
        const active = document.activeElement;
        if (captionField && captionField !== active) captionField.value = currentNode.attrs.imageCaption ?? '';
        if (altField && altField !== active) altField.value = currentNode.attrs.imageAlt ?? '';
        render();
      };

      // Drafts alt text from the file just uploaded. Any failure (AI off, too large, Bedrock down) is simply
      // no suggestion; the field stays an ordinary one.
      const draftAltText = async file => {
        suggestion = '';
        if (!capabilities.alt_text_path) return;
        drafting = true;
        render();
        const data = await postFile(capabilities.alt_text_path, file, { post_id: capabilities.post_id || '' });
        if (destroyed) return;
        drafting = false;
        suggestion = (data?.alt_text || '').trim();
        render();
      };

      const upload = async file => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
          showError('Choose an image file.');
          return;
        }
        if (!capabilities.upload_path) return;
        showError('');
        uploading?.classList.remove('hidden');
        dropzone?.classList.add('hidden');
        const data = await postFile(capabilities.upload_path, file);
        if (destroyed) return;
        uploading?.classList.add('hidden');
        if (!data?.url) {
          showError('Could not upload the image.');
          render();
          return;
        }
        setAttrs({ imageSrc: data.url });
        draftAltText(file);
      };

      const choose = () => fileInput?.click();

      fileInput?.addEventListener('change', () => {
        upload(fileInput.files[0]);
        fileInput.value = '';
      });
      $('[data-choose]')?.addEventListener('click', choose);
      replaceButton?.addEventListener('click', choose);

      ['dragenter', 'dragover'].forEach(name => dropzone?.addEventListener(name, e => {
        e.preventDefault();
        if (dropzoneLabel) dropzoneLabel.textContent = 'Drop to upload';
      }));
      dropzone?.addEventListener('dragleave', () => {
        if (dropzoneLabel) dropzoneLabel.textContent = DROPZONE_LABEL;
      });
      dropzone?.addEventListener('drop', e => {
        e.preventDefault();
        if (dropzoneLabel) dropzoneLabel.textContent = DROPZONE_LABEL;
        upload(e.dataTransfer?.files?.[0]);
      });

      captionField?.addEventListener('input', e => setAttrs({ imageCaption: e.target.value }));
      altField?.addEventListener('input', e => setAttrs({ imageAlt: e.target.value }));

      $('[data-use]')?.addEventListener('click', () => {
        if (suggestion) setAttrs({ imageAlt: suggestion });
      });

      $('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      syncFromNode();
      views.setView(initialView({ hasContent: hasImage() }));

      return {
        dom,
        // Typing in the fields must not reach ProseMirror (Backspace/Enter would act on the node), and the
        // drop must stay with the dropzone rather than becoming an editor-level image drop.
        stopEvent: event => Boolean(event.target?.closest?.('input, textarea, button, a, [data-dropzone]')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'imageBlock') return false;
          currentNode = updatedNode;
          syncFromNode();
          schedulePreview();
          return true;
        },
        destroy() {
          destroyed = true;
          clearTimeout(previewTimer);
        },
      };
    };
  },

  addCommands() {
    return {
      insertImageBlock: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

export const imageSlashItem = {
  title: 'Image',
  description: 'A photo with a caption and alt text',
  icon: '🖼',
  group: 'Media',
  keywords: ['image', 'photo', 'picture', 'caption', 'alt', 'figure', 'upload'],
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertImageBlock().run();
  },
};
